import type { UserImageProps as UIKitUserImageProps } from "@circle-react-uikit/UserImage";
import { UserImage as UIKitUserImage } from "@circle-react-uikit/UserImage";
import { isNoNamePlaceholder } from "@circle-react-uikit/UserImage/isNoNamePlaceholder";
import { UserImageStatus } from "@circle-react-uikit/UserImageStatus";
import { UserImageWithFallback as UIKitUserImageWithFallback } from "@circle-react-uikit/UserImageWithFallback";
import { Avatar as CompassAvatar } from "@circleco/compass/components/Avatar";
import { allowedClassesFor } from "@circleco/compass/helpers/allowedClassesFor";
import { omit } from "lodash";
import type { ComponentPropsWithRef } from "react";

import { userStatusToAvatarStatus } from "@/react/helpers/compass/userStatusToAvatarStatus";
import type { UserOnlineStatus } from "@/react/helpers/compass/userStatusToAvatarStatus";
import { useIsCircleV4FeatureEnabled } from "@/react/hooks/useIsCircleV4FeatureEnabled";

// eslint-disable-next-line no-barrel-files/no-barrel-files -- This is a compatibility layer that re-exports types for SettingsApp
export type { UserImageProps } from "@circle-react-uikit/UserImage";
// eslint-disable-next-line no-barrel-files/no-barrel-files -- This is a compatibility layer that re-exports types for SettingsApp
export type { UserImageStatusProps } from "@circle-react-uikit/UserImageStatus";

type CompassAvatarProps = ComponentPropsWithRef<typeof CompassAvatar>;

export interface UserImageWithStatusProps extends UIKitUserImageProps {
  /** If true, shows empty avatar image when name/src is empty or name is "no_name". Defaults to true. */
  shouldUseFallbackImage?: boolean;
  /** Optional status indicator (only works with Compass Avatar) */
  status?: UserOnlineStatus;
}

// Slack discussion for Avatar: https://circleso.slack.com/archives/C0A5QPWP5DL/p1768561887419079

const mapSize = (
  size: UIKitUserImageProps["size"] | undefined,
): CompassAvatarProps["size"] => {
  switch (size) {
    case "5":
      return "xs";
    case "6":
      return "sm";
    case "7":
      return "sm";
    case "8":
      return "md";
    case "9":
      return "lg";
    case "10":
      return "lg";
    case "14":
      return "xl";
    case "16":
      return "2xl";
    case "24":
      return "3xl";
    default:
      return "md";
  }
};

const UserImage = (props: UserImageWithStatusProps) => {
  const isCircleV4 = useIsCircleV4FeatureEnabled("circle_v4_avatars_enabled");
  const { shouldUseFallbackImage = true } = props;

  if (isCircleV4) {
    const { size, name, src, status } = props;

    const compassSize = mapSize(size);
    const compassStatus = userStatusToAvatarStatus(status);
    const isUserWithoutName = !name?.trim() || isNoNamePlaceholder(name);

    const compassProps: CompassAvatarProps = {
      ...omit(props, [
        "size",
        "hasBorder",
        "isFullRounded",
        "status",
        "shouldUseFallbackImage",
      ]),
      src: isUserWithoutName && shouldUseFallbackImage ? undefined : src,
      size: compassSize,
      status: compassStatus,
      name: isUserWithoutName ? "" : name,
      alt: isUserWithoutName ? undefined : name,
    };
    const className = compassProps.className
      ? allowedClassesFor("Avatar", compassProps.className)
      : "";

    return <CompassAvatar className={className} {...compassProps} />;
  }

  const { status } = props;

  if (status) {
    return (
      <UserImageStatus
        isFullRounded
        {...omit(props, ["shouldUseFallbackImage"])}
      />
    );
  }

  if (shouldUseFallbackImage) {
    return (
      <UIKitUserImageWithFallback
        isFullRounded
        {...omit(props, ["status", "shouldUseFallbackImage"])}
      />
    );
  }

  return (
    <UIKitUserImage
      isFullRounded
      {...omit(props, ["status", "shouldUseFallbackImage"])}
    />
  );
};

UserImage.displayName = "UserImage";

// eslint-disable-next-line no-barrel-files/no-barrel-files -- This is a compatibility layer that re-exports types for SettingsApp
export { UserImage, UserImageStatus };
